import {
  combineFilters,
  createActivePathFilter,
  createContentFilter,
  createReportingUnitFilter,
} from "lib/options/instanceTreeFiltering";
import type {
  DerivedShowroomOptions,
  ShowroomInstanceNode,
  ShowroomOptionCatalogFingerprints,
  ShowroomOptionCatalogSnapshot,
  ShowroomOptionLayout,
  ShowroomOptionSet,
} from "../state/showroomTypes";

const MASS_EDIT_FINGERPRINT_PREFIX = "massEdit:";

/** True when a layout has at least one option set, regardless of flat vs category shape. */
export function optionLayoutHasSets(layout: ShowroomOptionLayout): boolean {
  if (layout.kind === "flat") {
    return layout.optionSets.length > 0;
  }
  return layout.pages.some((page) => page.secondaryGroups.some((group) => group.optionSets.length > 0));
}

/** Restores the executable tree filter intentionally omitted from worker transport. */
export function hydrateShowroomOptionCatalogSnapshot(snapshot: ShowroomOptionCatalogSnapshot): DerivedShowroomOptions {
  const { instanceTreeFilterInputs, ...catalog } = snapshot;
  if (!instanceTreeFilterInputs) {
    return catalog;
  }

  const reportingUnitFilter = instanceTreeFilterInputs.hasReportingUnits
    ? createReportingUnitFilter(instanceTreeFilterInputs.selectedReportingUnitId)
    : null;
  return {
    ...catalog,
    instanceTreeFilter: combineFilters(
      createActivePathFilter(new Set(instanceTreeFilterInputs.activeInstancePaths)),
      createContentFilter((node: ShowroomInstanceNode) => optionLayoutHasSets(node.optionLayout)),
      reportingUnitFilter,
    ),
  };
}

interface OptionSetRevision {
  readonly content: string;
  readonly token: string;
}

/** Keeps exact comparisons in the worker and sends compact equality tokens with each full snapshot. */
export class ShowroomOptionCatalogFingerprinter {
  private entries: Map<string, OptionSetRevision> = new Map();
  private revision: number = 0;

  /** Scope must uniquely identify this session initialization, including worker restarts. */
  constructor(private readonly scope: string) {}

  /** Unchanged entries retain tokens; removed entries are pruned without recycling their revisions. */
  fingerprint(snapshot: ShowroomOptionCatalogSnapshot): ShowroomOptionCatalogFingerprints {
    const fingerprints: ShowroomOptionCatalogFingerprints = {};
    const nextEntries: Map<string, OptionSetRevision> = new Map();
    const add = (key: string, optionSet: ShowroomOptionSet): void => {
      const content: string = JSON.stringify(optionSet);
      let entry: OptionSetRevision | undefined = this.entries.get(key);
      if (entry?.content !== content) {
        if (this.revision === Number.MAX_SAFE_INTEGER) {
          throw new Error("Showroom option catalog revisions exhausted");
        }
        this.revision += 1;
        entry = { content, token: `${this.scope}:${this.revision}` };
      }
      nextEntries.set(key, entry);
      fingerprints[key] = entry.token;
    };
    for (const [optionSetId, optionSet] of Object.entries(snapshot.optionSetsTable)) {
      add(optionSetId, optionSet);
    }
    for (const optionSet of snapshot.massEditOptionSets) {
      add(MASS_EDIT_FINGERPRINT_PREFIX + optionSet.id, optionSet);
    }
    this.entries = nextEntries;
    return fingerprints;
  }
}

function arraysShareItems<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

function recordsShareItems<T>(left: Readonly<Record<string, T>>, right: Readonly<Record<string, T>>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((key) => left[key] === right[key]);
}

function reconcileLayout(
  previous: ShowroomOptionLayout | undefined,
  next: ShowroomOptionLayout,
  canonicalOptionSets: Readonly<Record<string, ShowroomOptionSet>>,
): ShowroomOptionLayout {
  if (next.kind === "flat") {
    const optionSets = next.optionSets.map((optionSet) => canonicalOptionSets[optionSet.id] ?? optionSet);
    if (previous?.kind === "flat" && arraysShareItems(previous.optionSets, optionSets)) {
      return previous;
    }
    return { ...next, optionSets };
  }

  const previousPages = previous?.kind === "category" ? previous.pages : [];
  const pages = next.pages.map((page, pageIndex) => {
    const previousPage = previousPages[pageIndex];
    const groups = page.secondaryGroups.map((group, groupIndex) => {
      const previousGroup = previousPage?.secondaryGroups[groupIndex];
      const optionSets = group.optionSets.map((optionSet) => canonicalOptionSets[optionSet.id] ?? optionSet);
      if (
        previousGroup?.secondaryKey === group.secondaryKey &&
        previousGroup.secondaryName === group.secondaryName &&
        arraysShareItems(previousGroup.optionSets, optionSets)
      ) {
        return previousGroup;
      }
      return { ...group, optionSets };
    });
    if (
      previousPage?.primaryKey === page.primaryKey &&
      previousPage.primaryName === page.primaryName &&
      arraysShareItems(previousPage.secondaryGroups, groups)
    ) {
      return previousPage;
    }
    return { ...page, secondaryGroups: groups };
  });
  if (previous?.kind === "category" && arraysShareItems(previous.pages, pages)) {
    return previous;
  }
  return { ...next, pages };
}

function reconcileTree(
  previous: ShowroomInstanceNode | undefined,
  next: ShowroomInstanceNode,
  canonicalOptionSets: Readonly<Record<string, ShowroomOptionSet>>,
): ShowroomInstanceNode {
  const matchingPrevious = previous?.id === next.id && previous.path === next.path ? previous : undefined;
  const optionLayout = reconcileLayout(matchingPrevious?.optionLayout, next.optionLayout, canonicalOptionSets);
  const children = next.children.map((child, index) =>
    reconcileTree(matchingPrevious?.children[index], child, canonicalOptionSets),
  );
  if (
    matchingPrevious &&
    matchingPrevious.name === next.name &&
    matchingPrevious.componentId === next.componentId &&
    matchingPrevious.reportingUnitId === next.reportingUnitId &&
    matchingPrevious.optionLayout === optionLayout &&
    arraysShareItems(matchingPrevious.children, children)
  ) {
    return matchingPrevious;
  }
  return { ...next, optionLayout, children };
}

/** Reuses unchanged option-set and straightforward parent references using worker fingerprints. */
export function reconcileShowroomOptionCatalogSnapshot(
  previous: ShowroomOptionCatalogSnapshot | null,
  previousFingerprints: ShowroomOptionCatalogFingerprints | null,
  next: ShowroomOptionCatalogSnapshot,
  nextFingerprints: ShowroomOptionCatalogFingerprints,
): ShowroomOptionCatalogSnapshot {
  const reconciledOptionSetsTable: Record<string, ShowroomOptionSet> = {};
  for (const [optionSetId, optionSet] of Object.entries(next.optionSetsTable)) {
    const previousOptionSet = previous?.optionSetsTable[optionSetId];
    const nextFingerprint = nextFingerprints[optionSetId];
    reconciledOptionSetsTable[optionSetId] =
      previousOptionSet && nextFingerprint !== undefined && previousFingerprints?.[optionSetId] === nextFingerprint
        ? previousOptionSet
        : optionSet;
  }

  const optionSetsTable =
    previous && recordsShareItems(previous.optionSetsTable, reconciledOptionSetsTable)
      ? previous.optionSetsTable
      : reconciledOptionSetsTable;
  const previousMassEditOptionSets = new Map(
    previous?.massEditOptionSets.map((optionSet) => [optionSet.id, optionSet]),
  );
  const reconciledMassEditOptionSets = next.massEditOptionSets.map((optionSet) => {
    const previousOptionSet = previousMassEditOptionSets.get(optionSet.id);
    const fingerprintKey = MASS_EDIT_FINGERPRINT_PREFIX + optionSet.id;
    return previousOptionSet && previousFingerprints?.[fingerprintKey] === nextFingerprints[fingerprintKey]
      ? previousOptionSet
      : optionSet;
  });
  const massEditOptionSets =
    previous && arraysShareItems(previous.massEditOptionSets, reconciledMassEditOptionSets)
      ? previous.massEditOptionSets
      : reconciledMassEditOptionSets;

  const optionLayout = reconcileLayout(previous?.optionLayout, next.optionLayout, optionSetsTable);
  const instanceTree = next.instanceTree
    ? reconcileTree(previous?.instanceTree, next.instanceTree, optionSetsTable)
    : undefined;
  return { ...next, optionSetsTable, massEditOptionSets, optionLayout, instanceTree };
}
